import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db, firebaseReady } from '../lib/firebase';
import {
  defaultAddons,
  defaultFaqs,
  defaultGallery,
  defaultPackages,
  defaultServices,
  defaultSettings,
  defaultTestimonials,
} from '../lib/defaults';
import type { Addon, CustomService, Faq, GalleryItem, Package, SiteSettings, Testimonial } from '../lib/types';

interface SiteData {
  packages: Package[];
  addons: Addon[];
  services: CustomService[];
  faqs: Faq[];
  gallery: GalleryItem[];
  testimonials: Testimonial[];
  settings: SiteSettings;
  /** true while showing built-in demo content (Firestore empty/unconfigured) */
  usingDefaults: boolean;
}

const SiteDataContext = createContext<SiteData | null>(null);

/** Subscribe to a collection, falling back to defaults when empty or on error. */
function useCollectionWithFallback<T extends { id: string }>(
  name: string,
  fallback: T[],
  sort: (a: T, b: T) => number,
  buildQuery?: () => ReturnType<typeof query>,
) {
  const [items, setItems] = useState<T[]>(fallback);
  const [isDefault, setIsDefault] = useState(true);

  useEffect(() => {
    if (!firebaseReady) return;
    const q = buildQuery ? buildQuery() : collection(db(), name);
    const unsub = onSnapshot(
      q as any,
      (snap: any) => {
        if (snap.empty) {
          setItems(fallback);
          setIsDefault(true);
        } else {
          setItems(snap.docs.map((d: any) => ({ id: d.id, ...d.data() }) as T));
          setIsDefault(false);
        }
      },
      () => {
        // Firestore not created yet or offline — keep defaults.
        setItems(fallback);
        setIsDefault(true);
      },
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { items: [...items].sort(sort), isDefault };
}

export function SiteDataProvider({ children }: { children: ReactNode }) {
  const byOrder = (a: { order: number }, b: { order: number }) => a.order - b.order;
  const byNewest = (a: { createdAt: number }, b: { createdAt: number }) => b.createdAt - a.createdAt;

  const packages = useCollectionWithFallback<Package>('packages', defaultPackages, byOrder);
  const addons = useCollectionWithFallback<Addon>('addons', defaultAddons, byOrder);
  const services = useCollectionWithFallback<CustomService>('customServices', defaultServices, byOrder);
  const faqs = useCollectionWithFallback<Faq>('faqs', defaultFaqs, byOrder);
  const gallery = useCollectionWithFallback<GalleryItem>('gallery', defaultGallery, byNewest);
  // Demo testimonials only appear in full demo mode (nothing seeded yet) —
  // once real content is seeded, an empty collection shows no reviews rather
  // than fake ones.
  const testimonials = useCollectionWithFallback<Testimonial>(
    'testimonials',
    [],
    byNewest,
    () => query(collection(db(), 'testimonials'), where('approved', '==', true)),
  );

  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  useEffect(() => {
    if (!firebaseReady) return;
    return onSnapshot(
      doc(db(), 'settings', 'site'),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() as Partial<SiteSettings>;
          // Deep-merge nested objects so settings docs saved before a
          // feature existed can never leave the site with missing fields.
          setSettings({
            ...defaultSettings,
            ...data,
            travel: { ...defaultSettings.travel, ...(data.travel ?? {}) },
            booking: { ...defaultSettings.booking, ...(data.booking ?? {}) },
            notifications: { ...defaultSettings.notifications, ...(data.notifications ?? {}) },
            socials: { ...defaultSettings.socials, ...(data.socials ?? {}) },
            hours: { ...defaultSettings.hours, ...(data.hours ?? {}) },
          });
        }
      },
      () => setSettings(defaultSettings),
    );
  }, []);

  const value: SiteData = {
    packages: packages.items.filter((p) => p.active),
    addons: addons.items.filter((a) => a.active),
    services: services.items.filter((s) => s.active),
    faqs: faqs.items,
    gallery: gallery.items,
    testimonials:
      testimonials.items.length > 0
        ? testimonials.items.filter((t) => t.approved)
        : packages.isDefault
          ? defaultTestimonials
          : [],
    settings,
    usingDefaults: packages.isDefault,
  };

  return <SiteDataContext.Provider value={value}>{children}</SiteDataContext.Provider>;
}

export function useSiteData() {
  const ctx = useContext(SiteDataContext);
  if (!ctx) throw new Error('useSiteData must be used inside SiteDataProvider');
  return ctx;
}
